import React from "react";
import { AlertCircle, Loader2, Users } from "lucide-react";

export interface UserListItem {
  userId: number;
  name: string;
  email: string;
  updatedAt: Date | string;
  subRole: string;
  roleResourceId: number;
}

interface UsersTabProps {
  users: UserListItem[];
  loading: boolean;
  error: string | null;
  onCreatePrincipalUser: () => void;
  onAddUser: () => void;
  onUpdateRole: (userId: number, roleResourceId: number) => void;
}

export const UsersTab: React.FC<UsersTabProps> = ({
  users,
  loading,
  error,
  onCreatePrincipalUser,
  onAddUser,
  onUpdateRole,
}) => {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold text-gray-900">Users ({users?.length || 0})</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onCreatePrincipalUser}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Create Principal User
          </button>
          <button
            onClick={onAddUser}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-brand text-white rounded-md hover:bg-brand/80 transition-colors"
          >
            Add User
          </button>
        </div>
      </div>

      {error ? (
        <div className="flex justify-center items-center py-12">
          <div className="flex items-center gap-2 text-red-500">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </div>
      ) : loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="flex items-center gap-2">
            <Loader2 className="w-8 h-8 animate-spin text-brand" />
            <span className="text-gray-500">Loading...</span>
          </div>
        </div>
      ) : users && users.length > 0 ? (
        <div className="space-y-4">
          {users.map((user) => (
            <div
              key={user.userId}
              className="bg-white rounded-lg border border-cardborder hover:shadow-sm transition-shadow"
            >
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                      <Users className="w-6 h-6 text-gray-400" />
                    </div>
                    <div>
                      <h6 className="text-lg font-semibold text-gray-900 mb-1">{user.name}</h6>
                      <p className="text-sm text-gray-500">{user.email}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Updated on{" "}
                        {new Date(user.updatedAt).toLocaleString("en-US", {
                          year: "2-digit",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        }).replace(",", "")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="flex flex-col items-center border-r border-gray-200 pr-6">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-xs text-gray-500">Role</span>
                      </div>
                      <span className="text-sm font-medium text-gray-700 mt-1">{user.subRole}</span>
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => onUpdateRole(user.userId, user.roleResourceId)}
                        className="px-4 py-2 text-sm font-medium bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                      >
                        Update Role
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No users found.</p>
        </div>
      )}
    </div>
  );
};
